import { computed, type ComputedRef } from 'vue';
import type { HydratedSection } from '@pleased-npm/fe-customisation';
import { useGetPage } from './useGetPage';
import { useGetPageBlock } from './useGetPageBlock';
import { findSectionByKey, findSectionInDocument } from '../utils/document.utils';
import { composableTracker } from '../utils/composableTracker';

export interface GetPageSectionParams {
  registryId: string;
  sectionKey: string;
  blockKey?: string;
}

/**
 * Get a section from a page document
 * If blockKey is provided, searches only in that block
 * Otherwise, searches across all blocks in the page
 * @param params - Object containing registryId, sectionKey, and optional blockKey
 * @returns ComputedRef of HydratedSection with theme values applied, or null if page/block not loaded, section not found, or show is false
 */
export const useGetPageSection = ({
  registryId,
  sectionKey,
  blockKey,
}: GetPageSectionParams): ComputedRef<HydratedSection | null> => {
  const trackKey = `${registryId}/${blockKey ?? ''}/${sectionKey}`;
  composableTracker.track('useGetPageSection', 'call', trackKey);

  if (blockKey) {
    const block = useGetPageBlock({ registryId, blockKey });
    return computed(() => {
      composableTracker.track('useGetPageSection', 'computedEvaluated', trackKey);
      if (!block.value) {
        return null;
      }
      try {
        const section = findSectionByKey(block.value, sectionKey) as HydratedSection;
        if (section && section.show === false) {
          return null;
        }
        return section;
      } catch (error) {
        return null;
      }
    });
  } else {
    const page = useGetPage({ registryId });
    return computed(() => {
      composableTracker.track('useGetPageSection', 'computedEvaluated', trackKey);
      if (!page.value) {
        return null;
      }
      try {
        const section = findSectionInDocument(page.value, sectionKey) as HydratedSection;
        if (section && section.show === false) {
          return null;
        }
        return section;
      } catch (error) {
        return null;
      }
    });
  }
};
